import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import Skeleton from './Skeleton';

const Table = ({ data = [], columns = [], options = {}, loading = false }) => {
  const defaultOptions = {
    pagination: true,
    pageSize: 10,
    pageSizeOptions: [5, 10, 20, 50],
    showSearch: true,
    searchPlaceholder: 'Buscar...',
    emptyMessage: 'No hay datos disponibles',
    className: '',
    rowClassName: '',
    headerClassName: '',
    sortable: true,
  };

  const tableOptions = { ...defaultOptions, ...options };

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(tableOptions.pageSize);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'none' });
  const [animKey, setAnimKey] = useState(0);
  const prevSearchRef = useRef('');
  const prevPageRef = useRef(1);

  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    return Object.values(item).some(value =>
      value !== null && value !== undefined &&
      value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const sortedData = React.useMemo(() => {
    let items = [...filteredData];
    if (sortConfig.key && sortConfig.direction !== 'none') {
      items.sort((a, b) => {
        const av = a[sortConfig.key], bv = b[sortConfig.key];
        if (av == null) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (bv == null) return sortConfig.direction === 'ascending' ? 1 : -1;
        if (typeof av === 'number' && typeof bv === 'number')
          return sortConfig.direction === 'ascending' ? av - bv : bv - av;
        return sortConfig.direction === 'ascending'
          ? av.toString().localeCompare(bv.toString())
          : bv.toString().localeCompare(av.toString());
      });
    }
    return items;
  }, [filteredData, sortConfig]);

  const paginatedData = React.useMemo(() => {
    if (!tableOptions.pagination) return sortedData;
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize, tableOptions.pagination]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  useEffect(() => {
    setCurrentPage(1);
    if (prevSearchRef.current !== searchTerm) {
      setAnimKey(k => k + 1);
      prevSearchRef.current = searchTerm;
    }
  }, [data, searchTerm]);

  useEffect(() => {
    if (prevPageRef.current !== currentPage) {
      setAnimKey(k => k + 1);
      prevPageRef.current = currentPage;
    }
  }, [currentPage]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleSort = (key) => {
    if (!tableOptions.sortable) return;
    let direction = 'ascending';
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'ascending') direction = 'descending';
      else if (sortConfig.direction === 'descending') direction = 'none';
    }
    setSortConfig({ key, direction });
    setAnimKey(k => k + 1);
  };

  const renderSortIcon = (columnKey) => {
    if (!tableOptions.sortable) return null;
    if (sortConfig.key === columnKey) {
      if (sortConfig.direction === 'ascending')
        return <FaSortUp aria-hidden className="ml-1 inline text-fg" />;
      if (sortConfig.direction === 'descending')
        return <FaSortDown aria-hidden className="ml-1 inline text-fg" />;
    }
    return <FaSort aria-hidden className="ml-1 inline text-fg-faint/60" />;
  };

  const pageBtn = 'h-9 min-w-[36px] px-2 grid place-items-center rounded-full text-[13px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70 disabled:opacity-35 disabled:cursor-not-allowed';
  const ariaSort = (key) => (sortConfig.key === key && sortConfig.direction !== 'none' ? sortConfig.direction : undefined);

  return (
    <div className="w-full">
      <div className="flex flex-col md:flex-row justify-between mb-4 gap-3">
        {tableOptions.showSearch && (
          <input
            type="search"
            aria-label={tableOptions.searchPlaceholder}
            placeholder={tableOptions.searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full md:w-80 h-10 rounded-full bg-raised/60 border border-line px-4 text-sm text-fg placeholder:text-fg-faint hover:border-fg/20 focus:outline-none focus-visible:border-live focus-visible:ring-2 focus-visible:ring-live/30"
          />
        )}
        {tableOptions.pagination && (
          <label className="flex items-center gap-2 text-[13px] text-fg-dim">
            Filas
            <select
              className="h-9 rounded-full bg-raised/60 border border-line px-3 text-[13px] text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-live/60"
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
            >
              {tableOptions.pageSizeOptions.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        )}
      </div>

      <div className="overflow-x-auto rounded-3xl bg-panel ring-1 ring-line" style={{ boxShadow: 'var(--shadow)' }}>
        <table className={`min-w-full ${tableOptions.className}`}>
          <thead className="border-b border-line">
            <tr>
              {columns.map((column, index) => {
                const sortable = tableOptions.sortable && column.sortable !== false;
                return (
                  <th key={index} scope="col" aria-sort={sortable ? ariaSort(column.key) : undefined}
                    className={`px-4 h-11 text-left text-[12.5px] font-semibold text-fg-dim whitespace-nowrap ${column.headerClassName || ''}`} style={column.headerStyle}>
                    {sortable ? (
                      <button type="button" onClick={() => handleSort(column.key)} className="inline-flex items-center gap-1 rounded-md hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">
                        {column.title}{renderSortIcon(column.key)}
                      </button>
                    ) : column.title}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns.length} className="p-0"><Skeleton.TableRows rows={tableOptions.pageSize > 10 ? 8 : Math.min(tableOptions.pageSize, 8)} cols={columns.length} /></td></tr>
            ) : paginatedData.length > 0 ? (
              <AnimatePresence mode="sync">
                {paginatedData.map((row, rowIndex) => (
                  <motion.tr
                    key={`${animKey}-${rowIndex}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.25, delay: Math.min(rowIndex, 12) * 0.02 }}
                    className={`border-t border-line first:border-0 hover:bg-raised/50 transition-colors ${tableOptions.rowClassName}`}
                  >
                    {columns.map((column, colIndex) => (
                      <td key={colIndex} className={`px-4 py-3.5 text-sm text-fg-dim ${column.className || ''}`} style={column.style}>
                        {column.render ? column.render(row[column.key], row, rowIndex) : row[column.key]}
                      </td>
                    ))}
                  </motion.tr>
                ))}
              </AnimatePresence>
            ) : (
              <tr><td colSpan={columns.length} className="px-4 py-12 text-center text-sm text-fg-faint">{tableOptions.emptyMessage}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {tableOptions.pagination && totalPages > 0 && (
        <nav aria-label="Páginas" className="flex flex-wrap justify-between items-center gap-3 mt-4">
          <p className="text-[13px] text-fg-faint">{paginatedData.length} de {sortedData.length} resultados</p>
          <div className="flex items-center gap-1">
            <button type="button" aria-label="Página anterior" className={`${pageBtn} text-fg-dim hover:bg-raised`} onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
              <FaChevronLeft aria-hidden size={11} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) pageNum = i + 1;
              else if (currentPage <= 3) pageNum = i + 1;
              else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
              else pageNum = currentPage - 2 + i;
              const active = currentPage === pageNum;
              return (
                <button key={i} type="button" aria-current={active ? 'page' : undefined} onClick={() => handlePageChange(pageNum)}
                  className={`${pageBtn} ${active ? 'bg-fg text-ink' : 'text-fg-dim hover:bg-raised'}`}>{pageNum}</button>
              );
            })}
            <button type="button" aria-label="Página siguiente" className={`${pageBtn} text-fg-dim hover:bg-raised`} onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
              <FaChevronRight aria-hidden size={11} />
            </button>
          </div>
        </nav>
      )}
    </div>
  );
};

export default Table;
